import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import useEmblaCarousel from "embla-carousel-react";
import { ArrowRight, ChevronLeft, ChevronRight, Play, Mail, Phone, Eye } from "lucide-react";
import { fmtCount } from "../../lib/text";
import { RichHtml } from "./RichHtml";
import { VideoSlider } from "./VideoSlider";
import { HeroButtons } from "./HeroButtons";
import { SiteIcon } from "./SiteIcon";
import { useSite, usePublic } from "../../lib/site";
import { href, fmtDate, stripHtml } from "../../lib/i18n";
import { Img, Socials } from "./Common";
import { ContactForm } from "./ContactForm";
import { StyledText } from "./StyledText";

const FADE = "linear-gradient(to right, transparent 0%, #000 16%, #000 84%, transparent 100%), linear-gradient(to bottom, transparent 0%, #000 14%, #000 72%, transparent 100%)";
const card = "rounded-2xl border border-site-border bg-s-surface";

function SectionTitle({ eyebrow, title, aside, eyebrowStyle, titleStyle, testid, children }) {
  const { site } = useSite();
  if (!eyebrow && !title && !children && !aside) return null;
  return (
    <div className="border-b border-site-border pb-6 mb-10" data-testid={testid}>
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div className="flex-1 min-w-0">
          {eyebrow && <StyledText className="text-xs font-bold uppercase tracking-[0.25em] text-s-primary" style={eyebrowStyle} text={eyebrow} testid={testid && `${testid}-eyebrow`} />}
          {title && <StyledText as="h2" className="block text-3xl sm:text-4xl font-heading font-bold text-white mt-1" style={titleStyle || site.profile?.section_style} text={title} testid={testid && `${testid}-title`} />}
        </div>
        {aside}
      </div>
      {children}
    </div>
  );
}

function Hero() {
  const { site, t, lang, subtitle } = useSite();
  const p = site.profile || {};
  const H = p.hide || {};
  return (
    <section data-testid="hero-section" className={`relative min-h-[80vh] grid gap-8 sm:gap-10 items-center ${H.photo ? "" : "md:grid-cols-[1.1fr_1fr]"}`}>
      {!H.photo && <div className="relative order-1 md:order-2 w-full max-w-[420px] md:max-w-[560px] mx-auto rise" style={{ animationDelay: ".15s" }}>
        <div className="absolute inset-10 rounded-full bg-[#d4af3726] blur-[90px]" aria-hidden />
        <Img src={p.blog_photo || p.photo} alt={p.name} data-testid="hero-portrait" loading="eager" className={`hero-photo relative w-full aspect-[4/5] object-cover object-top ${p.hero_photo_motion !== false ? "hero-float" : ""}`}
          style={{ WebkitMaskImage: FADE, maskImage: FADE, WebkitMaskComposite: "source-in", maskComposite: "intersect", "--hm": Math.max(0, Math.min(2, (p.hero_photo_motion_intensity ?? 100) / 100)) }} />
      </div>}
      <div className="relative order-2 md:order-1 max-w-3xl space-y-8 rise">
        <div className="space-y-3">
          {t(p.badge) && !H.badge && (
            <div className="inline-flex items-center gap-3 px-4 py-1.5 rounded-full border border-[#d4af374d] bg-[#d4af370d] text-s-primary text-xs font-semibold uppercase tracking-widest" data-testid="hero-badge">
              <span className="w-1.5 h-1.5 rounded-full bg-s-primary animate-ping" /> {t(p.badge)}
            </div>
          )}
          {subtitle && !H.tagline && <div className="text-xs uppercase tracking-[0.4em] font-heading text-slate-400 font-semibold pt-2" data-testid="hero-tagline">{subtitle}</div>}
        </div>
        {t(p.headline) && !H.headline && (
          <StyledText as="h1" className="text-3xl sm:text-5xl font-heading font-bold text-white leading-tight tracking-tight" bClass="text-gold" style={p.headline_style} text={t(p.headline)} tail={2} testid="hero-headline" />
        )}
        {!H.intro && <p className="text-slate-400 text-base sm:text-lg leading-relaxed font-light max-w-2xl">{t(p.intro)}</p>}
        {!H.buttons && <HeroButtons buttons={p.hero_buttons} />}
      </div>
    </section>
  );
}

function Disciplines() {
  const { site, lang, t } = useSite();
  const p = site.profile || {};
  const H = p.hide || {};
  const { data } = usePublic(["public-services"], "/public/services");
  const items = (data?.items || []);
  const [ref, api] = useEmblaCarousel({ align: "start", loop: items.length > 4, slidesToScroll: "auto", dragFree: true });
  const [paused, setPaused] = useState(false);
  useEffect(() => {
    if (!api || paused || items.length <= 4) return;
    const id = setInterval(() => api.scrollNext(), 4000);
    return () => clearInterval(id);
  }, [api, paused, items.length]);
  if (!items.length || H.disciplines) return null;
  const worksHref = `${lang === "tr" ? "" : "/" + lang}/calismalarim`;
  const worksLabel = lang === "tr" ? "Çalışmalarımı Keşfet" : "Explore my works";
  const arrow = "w-10 h-10 rounded-full border border-site-border bg-[#11131980] backdrop-blur flex items-center justify-center text-white hover:bg-s-primary hover:text-s-onprimary hover:border-transparent transition-colors";
  return (
    <section id="disiplinler" data-testid="section-disciplines" className="scroll-mt-28">
      <SectionTitle testid="disc-head" eyebrow={H.disc_eyebrow ? "" : t(p.disc_eyebrow) || (lang === "tr" ? "Çok Yönlü Portföy" : "Portfolio")}
        title={H.disc_title ? "" : t(p.disc_title) || (lang === "tr" ? "Ne Yaptım? Ne Yaparım? Ne Yapacağım?" : "What I did, do and will do")}
        eyebrowStyle={p.disc_eyebrow_style} titleStyle={p.disc_title_style}
        aside={<Link to={worksHref} data-testid="disc-head-works-link" className="hidden sm:inline-flex items-center gap-2 text-sm font-semibold text-s-primary hover:text-white transition-colors">{worksLabel} <ArrowRight size={15} /></Link>}>
        {!H.disc_intro && t(p.disc_intro) ? <RichHtml html={t(p.disc_intro)} className="mt-5 text-slate-300" testid="disc-intro" /> : null}
      </SectionTitle>
      <div className="relative" data-testid="disciplines-works-slider" onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)}>
        <div className="overflow-hidden" ref={ref}>
          <div className="flex -ml-4">
            {items.map((s) => {
              const title = t(s.card_title) || t(s.title);
              return (
                <div key={s.id} className="min-w-0 shrink-0 grow-0 pl-4 basis-1/2 sm:basis-1/3 lg:basis-1/4 xl:basis-1/5">
                  <Link
                    to={`/calismalarim/${s.slug}`}
                    data-testid={`disciplines-work-${s.slug}`}
                    className="group relative block rounded-2xl overflow-hidden border border-site-border bg-[#11131980] hover:border-[#d4af3766] transition-all"
                  >
                    <div className="relative aspect-[16/9] overflow-hidden bg-[#0b0c10]">
                      {s.cover ? (
                        <Img src={s.cover} alt={title} loading="lazy" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.04]" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-s-primary">
                          <SiteIcon name={s.icon} size={38} />
                        </div>
                      )}
                      <div className="absolute inset-0 bg-gradient-to-t from-[#0b0c10f2] via-[#0b0c10cc] to-transparent" />
                    </div>
                    <div className="absolute inset-x-0 bottom-0 p-3">
                      <h4 className="text-sm font-semibold text-white leading-snug line-clamp-2 group-hover:text-s-primary transition-colors">
                        {title}
                      </h4>
                    </div>
                  </Link>
                </div>
              );
            })}
          </div>
        </div>
        {items.length > 4 && (
          <>
            <button type="button" onClick={() => api?.scrollPrev()} aria-label="Önceki" data-testid="disciplines-prev"
              className={`${arrow} absolute top-1/2 -translate-y-1/2 -left-4 sm:-left-5 z-10`}><ChevronLeft size={18} /></button>
            <button type="button" onClick={() => api?.scrollNext()} aria-label="Sonraki" data-testid="disciplines-next"
              className={`${arrow} absolute top-1/2 -translate-y-1/2 -right-4 sm:-right-5 z-10`}><ChevronRight size={18} /></button>
          </>
        )}
      </div>
      <div className="mt-6 flex sm:hidden">
        <Link to={worksHref} className="inline-flex items-center gap-2 text-sm font-semibold text-s-primary" data-testid="disc-mobile-works-link">
          {worksLabel} <ArrowRight size={15} />
        </Link>
      </div>
    </section>
  );
}

function Media({ videos }) {
  const { site, lang } = useSite();
  if (!videos?.length || site.profile?.hide?.media) return null;
  return (
    <section id="videolar" className="scroll-mt-28" data-testid="section-featured-albums">
      <SectionTitle eyebrow={lang === "tr" ? "Sinematik Portföy" : "Cinematic Portfolio"} title={lang === "tr" ? "Video Galerisi" : "Video Gallery"}
        aside={<Link to={href(lang, "videos")} className="text-sm text-s-primary hover:underline" data-testid="home-all-videos-link">{lang === "tr" ? "Tüm videolar →" : "All videos →"}</Link>} />
      <VideoSlider videos={videos} config={site.profile?.video_slider} />
    </section>
  );
}

function NewsMeta({ n, className = "" }) {
  const { lang, ui } = useSite();
  return (
    <div className={`flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-500 ${className}`}>
      <span>{fmtDate(n.published_at, lang)}</span>
      <span className="w-1 h-1 rounded-full bg-slate-600" />
      <span>{n.minutes || 1} {ui.minRead}</span>
      {n.views > 0 && <><span className="w-1 h-1 rounded-full bg-slate-600" /><span className="flex items-center gap-1"><Eye size={12} />{fmtCount(n.views, lang)}</span></>}
    </div>
  );
}

function FeaturedPost({ n }) {
  const { lang, t } = useSite();
  return (
    <Link to={href(lang, "news", n.slug)} data-testid={`news-card-${n.slug}`} className="group lg:col-span-7 flex flex-col rounded-3xl border border-site-border bg-[#111319] overflow-hidden hover:border-[#d4af3766] transition-colors duration-500">
      <div className="relative aspect-[16/9] overflow-hidden bg-[#0b0c10]">
        {n.cover && <Img src={n.cover} alt={t(n.title)} className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-700" />}
        <div className="absolute inset-0 bg-gradient-to-t from-[#111319] via-transparent to-transparent" />
        {n.category && <span className="absolute top-5 left-5 px-3 py-1 rounded-full bg-[#090a0dcc] backdrop-blur text-[11px] font-semibold uppercase tracking-wider text-s-primary">{t(n.category.name)}</span>}
      </div>
      <div className="p-7 sm:p-8 -mt-10 relative space-y-3">
        <NewsMeta n={n} />
        <h4 className="text-2xl sm:text-3xl font-heading font-bold text-white leading-snug group-hover:text-s-primary transition-colors">{t(n.title)}</h4>
        <p className="text-slate-400 leading-relaxed line-clamp-3">{t(n.summary) || t(n.seo?.description)}</p>
        <span className="inline-flex items-center gap-2 pt-2 text-sm font-semibold text-s-primary">{lang === "tr" ? "Yazıyı oku" : "Read article"} <ArrowRight size={15} className="group-hover:translate-x-1 transition-transform" /></span>
      </div>
    </Link>
  );
}

function PostRow({ n, index }) {
  const { lang, t } = useSite();
  return (
    <Link to={href(lang, "news", n.slug)} data-testid={`news-card-${n.slug}`} className="group flex gap-5 py-5 first:pt-0 last:pb-0">
      <span className="font-heading text-2xl font-bold text-[#d4af37]/35 group-hover:text-s-primary transition-colors leading-none pt-1 w-9 shrink-0 tabular-nums">{String(index).padStart(2, "0")}</span>
      <div className="min-w-0 space-y-2">
        {n.category && <span className="text-[11px] font-semibold uppercase tracking-wider text-s-primary">{t(n.category.name)}</span>}
        <h5 className="text-lg font-bold text-white leading-snug line-clamp-2 group-hover:text-s-primary transition-colors">{t(n.title)}</h5>
        <NewsMeta n={n} />
      </div>
    </Link>
  );
}

function Writings({ news }) {
  const { site, lang, ui } = useSite();
  if (site.profile?.hide?.writings || !news?.length) return null;
  const [first, ...rest] = news;
  return (
    <section id="yazilar" className="scroll-mt-28" data-testid="section-latest-news">
      <SectionTitle eyebrow={lang === "tr" ? "Köşe Yazıları & Düşünce" : "Columns & Ideas"} title={ui.latestNews}
        aside={<Link to={href(lang, "news")} className="text-sm text-s-primary hover:underline" data-testid="home-all-news-link">{ui.allNews} →</Link>} />
      <div className="grid lg:grid-cols-12 gap-10 items-start">
        <FeaturedPost n={first} />
        <div className="lg:col-span-5 lg:self-stretch flex flex-col gap-6" data-testid="news-list">
          <div className="flex-1 flex flex-col justify-evenly divide-y divide-[color:var(--s-border)]">{rest.slice(0, 3).map((n, i) => <PostRow key={n.id} n={n} index={i + 2} />)}</div>
          <Link to={href(lang, "news")} data-testid="news-list-cta" className="group rounded-2xl border border-dashed border-[#d4af374d] p-6 flex items-center justify-between gap-4 hover:border-s-primary hover:bg-[#d4af370d] transition-colors">
            <div>
              <p className="text-xs uppercase tracking-[0.2em] text-s-primary font-semibold">{lang === "tr" ? "Arşiv" : "Archive"}</p>
              <p className="mt-1 font-heading text-lg text-white">{lang === "tr" ? "Tüm yazıları keşfedin" : "Explore all articles"}</p>
            </div>
            <span className="w-11 h-11 rounded-full bg-s-primary text-s-onprimary flex items-center justify-center group-hover:translate-x-1 transition-transform"><ArrowRight size={17} /></span>
          </Link>
        </div>
      </div>
    </section>
  );
}

function Contact() {
  const { site, t, lang } = useSite();
  const g = site.general || {};
  const H = g.hide || {};
  if (site.profile?.hide?.contact) return null;
  return (
    <section id="iletisim" className="scroll-mt-28 grid lg:grid-cols-12 gap-12 items-stretch pt-8 border-t border-site-border" data-testid="section-contact-cta">
      <div className={`${H.form ? "lg:col-span-12" : "lg:col-span-5 lg:py-[33px]"} flex flex-col gap-6`} data-testid="contact-info-col">
        {!H.contact_eyebrow && t(g.contact_eyebrow) && <StyledText className="block text-xs font-bold uppercase tracking-[0.25em] text-s-primary" style={g.contact_eyebrow_style} text={t(g.contact_eyebrow)} testid="contact-eyebrow" />}
        {!H.contact_title && <StyledText as="h3" className="block text-3xl font-heading font-bold text-white" style={g.contact_title_style || site.profile?.section_style} testid="contact-title"
          text={t(g.contact_title) || (lang === "tr" ? "İletişim ve İş Birliği" : "Contact & Collaboration")} />}
        {!H.contact_text && (t(g.contact_text) ? <RichHtml html={t(g.contact_text)} className="text-slate-400 text-sm" testid="contact-text" /> :
          <p className="text-slate-400 text-sm leading-relaxed" data-testid="contact-text">{lang === "tr" ? "Projeler, konuşmacı davetleri, kitap talepleri ve iş birlikleri için doğrudan yazabilirsiniz." : "Write to me directly for projects, speaking invitations and collaborations."}</p>)}
        <div className="space-y-3 text-sm text-slate-300">
          {g.email && !H.email && <a href={`mailto:${g.email}`} className="flex items-center gap-3 hover:text-s-primary transition-colors" data-testid="contact-email"><Mail size={15} className="text-s-primary" />{g.email}</a>}
          {g.phone && !H.phone && <a href={`tel:${g.phone.replace(/\s/g, "")}`} className="flex items-center gap-3 hover:text-s-primary transition-colors" data-testid="contact-phone"><Phone size={15} className="text-s-primary" />{g.phone}</a>}
        </div>
        {!H.socials && <Socials socials={site.profile?.socials} className="pt-2 lg:mt-auto" />}
      </div>
      {!H.form && <div className="lg:col-span-7 p-8 rounded-3xl border border-site-border bg-[#111319cc] backdrop-blur-xl" data-testid="contact-form-box"><ContactForm type="contact" /></div>}
    </section>
  );
}

export default function NormalHome({ data }) {
  return (
    <div className="relative">
      <div className="fixed top-[-10%] right-[-5%] w-[600px] h-[600px] bg-[#d4af371a] rounded-full blur-[140px] pointer-events-none" aria-hidden />
      <div className="fixed bottom-[-10%] left-[-5%] w-[600px] h-[600px] bg-[#1e3a8a1a] rounded-full blur-[160px] pointer-events-none" aria-hidden />
      <div className="relative max-w-7xl mx-auto px-6 space-y-36 py-10 md:py-16">
        <Hero />
        <Disciplines />
        <Media videos={data?.videos} />
        <Writings news={data?.news} />
        <Contact />
      </div>
    </div>
  );
}
